/**
 * The three keys and the teams behind them.
 *
 * A row is a digit and a name, and the name is FREE TEXT with suggestions
 * rather than a select. The two mistakes are not symmetric: a department this
 * desk has not filed a ticket under yet must still be typeable, and a name that
 * differs from the one in use by one letter is a call no saved filter ever
 * finds. The suggestions come from the departments already in use, which is the
 * same list the ticket combobox opens with.
 *
 * The digit is a select rather than a text box. It is one of nine keys on a
 * phone, and a duplicate is dropped by the server rather than renumbered -
 * renumbering would move a team onto a key the recorded question does not name.
 */
import { Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { CallMenuDepartment } from "../../../../lib/calls";
import { Btn, IconBtn } from "../../../ui";
import { Pill, SelectPill } from "../panel/fields";

/** The keys a phone has that a menu may use. Zero is reserved for an operator. */
const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

const DATALIST_ID = "crm-menu-departments";

export function MenuDepartmentRows({
  rows,
  onChange,
  suggestions,
  max,
}: {
  rows: CallMenuDepartment[];
  onChange: (next: CallMenuDepartment[]) => void;
  /** Departments already in use, commonest first. May be empty. */
  suggestions: string[];
  max: number;
}) {
  const t = useT();

  const patch = (index: number, next: Partial<CallMenuDepartment>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...next } : row)));

  // The first key nobody has taken, so adding a row does not open on a digit
  // that will be dropped the moment it is saved.
  const freeDigit = DIGITS.find((d) => !rows.some((r) => r.digit === d)) ?? "1";

  return (
    <div className="grid gap-2.5">
      <datalist id={DATALIST_ID}>
        {suggestions.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>

      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2.5">
          <SelectPill
            value={row.digit}
            onChange={(digit) => patch(i, { digit })}
            label={t("settings.menu.digit")}
            className="w-20 shrink-0"
          >
            {DIGITS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </SelectPill>
          <Pill
            value={row.department}
            onChange={(department) => patch(i, { department })}
            placeholder={t("settings.menu.departmentPlaceholder")}
            list={DATALIST_ID}
            className="flex-1"
          />
          <IconBtn
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            aria-label={t("settings.menu.removeDepartment")}
          >
            <Trash2 className="size-4" />
          </IconBtn>
        </div>
      ))}

      {rows.length < max ? (
        <div>
          <Btn small onClick={() => onChange([...rows, { digit: freeDigit, department: "" }])}>
            {t("settings.menu.addDepartment")}
          </Btn>
        </div>
      ) : (
        <p className="crm-sub !mt-0 text-[11.5px]">{t("settings.menu.departmentsFull", { n: max })}</p>
      )}
    </div>
  );
}
